import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";

const TESTIMONIALS = [
  { name: "Tosin A.", role: "Fragrance Enthusiast", avatar: "T", rating: 5, text: "Tiwani makes it so easy to find my favourite brands in one place - every scent feels authentic and classy. I won't shop anywhere else." },
  { name: "Michael A.", role: "Returning Customer", avatar: "M", rating: 5, text: "Every perfume I've bought from Tiwani is original and lasts beautifully. Their packaging and service are consistently top-notch." },
  { name: "Leila R.", role: "Luxury Lover", avatar: "L", rating: 5, text: "Shopping here feels premium from the scent selection to the elegant delivery. Tiwani has completely redefined my fragrance experience." },
  { name: "Funke O.", role: "First-time Buyer", avatar: "F", rating: 5, text: "I was sceptical about ordering online, but the quality exceeded my expectations. Fast delivery and perfectly packaged. Will definitely be back." },
  { name: "David K.", role: "Gift Shopper", avatar: "D", rating: 5, text: "Bought as a birthday gift and my partner was absolutely thrilled. The packaging alone is stunning - truly a luxury experience." },
];

function Stars({ count }) {
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, index) => (
        <Star key={index} size={13} className={index < count ? "text-yellow-400 fill-yellow-400" : "text-gray-300"} />
      ))}
    </div>
  );
}

export default function Testimonials() {
  const [active, setActive] = useState(0);
  const next = useCallback(() => setActive((previous) => (previous + 1) % TESTIMONIALS.length), []);
  const prev = useCallback(() => setActive((previous) => (previous - 1 + TESTIMONIALS.length) % TESTIMONIALS.length), []);

  useEffect(() => {
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [next]);

  const cards = [0, 1, 2].map((offset) => ({
    data: TESTIMONIALS[(active + offset) % TESTIMONIALS.length],
    isCenter: offset === 1,
  }));
  const mobileCard = TESTIMONIALS[active];

  return (
    <section className="py-24 bg-brand-dark overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16">
          <span className="section-label">Social Proof</span>
          <h2 className="font-display text-4xl sm:text-5xl text-white font-light mt-2">
            What Our Customers Say
          </h2>
          <p className="font-body text-sm text-white/55 mt-4">Trusted by customers who value authenticity, service, and fast delivery.</p>
        </div>

        <div className="md:hidden">
          <div className="p-8 rounded-sm border border-transparent bg-white transition-all duration-500">
            <Stars count={mobileCard.rating} />
            <p className="font-body text-sm leading-relaxed mt-4 mb-6 text-brand-charcoal">
              "{mobileCard.text}"
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center font-display font-bold text-lg bg-brand-red text-white">
                {mobileCard.avatar}
              </div>
              <div>
                <p className="font-body font-semibold text-sm text-brand-dark">{mobileCard.name}</p>
                <p className="font-body text-xs text-gray-400">{mobileCard.role}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="hidden md:grid md:grid-cols-3 gap-6">
          {cards.map(({ data, isCenter }, index) => (
            <div
              key={`${data.name}-${index}`}
              className={`p-8 rounded-sm border transition-all duration-500 ${
                isCenter ? "bg-white border-transparent" : "bg-white/5 border-white/10"
              }`}
            >
              <Stars count={data.rating} />
              <p className={`font-body text-sm leading-relaxed mt-4 mb-6 ${isCenter ? "text-brand-charcoal" : "text-white/55"}`}>
                "{data.text}"
              </p>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-display font-bold text-lg ${isCenter ? "bg-brand-red text-white" : "bg-white/10 text-white"}`}>
                  {data.avatar}
                </div>
                <div>
                  <p className={`font-body font-semibold text-sm ${isCenter ? "text-brand-dark" : "text-white"}`}>{data.name}</p>
                  <p className={`font-body text-xs ${isCenter ? "text-gray-400" : "text-white/35"}`}>{data.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center gap-5 mt-10">
          <button onClick={prev} aria-label="Previous" className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-brand-red hover:bg-brand-red transition-all duration-300">
            <ChevronLeft size={17} />
          </button>
          <div className="flex gap-2">
            {TESTIMONIALS.map((_, index) => (
              <button
                key={index}
                onClick={() => setActive(index)}
                aria-label={`Show testimonial ${index + 1}`}
                className={`rounded-full transition-all duration-300 ${index === active ? "w-6 h-2 bg-brand-red" : "w-2 h-2 bg-white/25 hover:bg-white/50"}`}
              />
            ))}
          </div>
          <button onClick={next} aria-label="Next" className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-brand-red hover:bg-brand-red transition-all duration-300">
            <ChevronRight size={17} />
          </button>
        </div>
      </div>
    </section>
  );
}
